import { api } from '../utils/api';
import type {
  OrderItemInput,
  FulfillmentType,
  ServeMode,
  OrderDestination,
} from '../types/@types/order';


export interface CreateOrderPayload {
  table?: string;
  parentTableId?: string;
  orderSequence?: number;
  fulfillment_type?: FulfillmentType;
  meta?: { forTakeaway: boolean };
  destination?: OrderDestination;
  customerName?: string;
  customerPhone?: string;
  pickupEta?: string;
  items: {
    product: string | number;
    quantity: number;
    notes?: string;
    flavors?: { flavorId: string; qty: number }[];
    additions?: Record<string, number>;
    exceptions?: string[];
    fulfillment_type_override?: FulfillmentType;
    serveMode?: ServeMode;
  }[];
}

export async function createOrder(payload: CreateOrderPayload) {
  return api.post('/orders', payload);
}

export interface CreateTakeawayOrderPayload
  extends Omit<CreateOrderPayload, 'table' | 'destination'> {
  customerName: string;
  destination?: 'TAKEAWAY';
}

export async function createTakeawayOrder(payload: CreateTakeawayOrderPayload) {
  const body = {
    ...payload,
    destination: 'TAKEAWAY' as const,
  };
  return api.post('/orders/takeaway', body);
}

export interface GetOrdersParams {
  destination?: OrderDestination;
  q?: string;
  page?: number;
  paid?: boolean;
}

export async function getOrders(params?: GetOrdersParams) {
  const { data } = await api.get('/orders', {
    params: params && Object.keys(params).length > 0 ? params : undefined,
  });
  return data;
}

export async function payOrder(id: string | number) {
  if (!id) {
    throw new Error('Invalid order id');
  }
  const { data } = await api.patch(`/orders/${id}/pay`);
  return data;
}

export async function getOrder(id?: string | number) {
  if (!id) {
    throw new Error('Invalid order id');
  }
  const { data } = await api.get(`/orders/${id}`);
  return data;
}

export async function getOrdersByTable(tableId: string | number) {
  if (!tableId) {
    throw new Error('Invalid table id');
  }
  const { data } = await api.get(`/orders/table/${tableId}`);
  return Array.isArray(data) ? data : [];
}

export async function addItemsToOrder(
  id: string | number,
  payload: { items: CreateOrderPayload['items'] },
) {
  if (!id) {
    throw new Error('Invalid order id');
  }
  return api.post(`/orders/${id}/items`, payload);
}

export async function updateOrderStatus(
  id: string | number,
  status: 'IN_PRODUCTION' | 'DONE' | 'PAID' | 'DELIVERED',
) {
  if (!id) {
    throw new Error('Invalid order id');
  }
  const { data } = await api.put(`/orders/${id}/status`, { status });
  return data;
}

export async function priceCheck(payload: { items: OrderItemInput[] }) {
  const { data } = await api.post('/orders/price-check', payload);
  return data;
}

export async function updateOrderFulfillment(
  orderId: string | number,
  next: FulfillmentType,
) {
  if (!orderId) {
    throw new Error('Invalid order id');
  }
  return api.patch(`/orders/${orderId}`, {
    fulfillment_type: next,
  });
}

export async function updateItemFulfillmentOverride(
  orderId: string | number,
  itemId: string | number,
  next: FulfillmentType | null,
) {
  if (!orderId || !itemId) {
    throw new Error('Invalid ids');
  }
  return api.patch(`/orders/${orderId}/items/${itemId}`, {
    fulfillment_type_override: next,
  });
}

export async function sendPendingToKitchen(
  orderId: string | number,
  options?: { idempotencyKey?: string },
) {
  if (!orderId) {
    throw new Error('Invalid order id');
  }
  return api.post(
    `/orders/${orderId}/send-to-kitchen`,
    {},
    options?.idempotencyKey
      ? {
        headers: {
          'Idempotency-Key': options.idempotencyKey,
        },
      }
      : undefined,
  );
}
export async function splitOrder(
  orderId: string | number,
  itemIds: (string | number)[],
) {
  if (!orderId) {
    throw new Error('Invalid order id');
  }
  return api.post(`/orders/${orderId}/split`, { item_ids: itemIds });
}
